/* eslint-disable prettier/prettier */
import React, { FC, useCallback, useRef } from 'react';
import { View } from 'react-native';

import { scale as initialScale } from '@components/frame';

import { setModalMissingPIOpen } from '@reducers/machinesV2';
import { connect } from 'react-redux';
import { Machine } from '@interfaces/machines';
import HomePageHeader from '@components/operatorPanel/components/HomePage/HomePageHeader';
import HomePageBottomPart from '@components/operatorPanel/components/HomePage/HomePageBottomPart';
import NCFileJobsInfo from './elements/NCFileJobsInfo';
import HomePageTopRightPart from '@components/operatorPanel/components/HomePage/HomePageTopRightPart';
import HomePageCentralPart from '@components/operatorPanel/components/HomePage/HomePageCentralPart';
import { ScaleFunction, WrapperOverColorFunction } from '@components/operatorPanel/interfaces';
import { isWeb, isPhone, ncFileStatuses } from '@helpers';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { HomePageTopCentralPart } from '@components/operatorPanel/components/HomePage/HomePageTopCentralPart';
import { font as initialFont } from '@styles/fonts';
import { OPERATOR_PANEL_HEADER_HEIGHT, OPERATOR_PANEL_PADDING } from '@components/operatorPanel/constants/operatorPanel.constants';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';

export interface PanelProps {
  CC: WrapperOverColorFunction;
  selectedItem: Machine;
  bgColor: string;
  count: number;
  maxCount: number;
  isOperator?: boolean;
  productionBatchId?: number;
  insetTop: number;
  insetBottom: number;
  scale?: ScaleFunction;
  font?: typeof initialFont;
  correctQuantity: (number: number) => Promise<any>;
}

const Panel: FC<PanelProps> = (props) => {
  const {
    CC,
    selectedItem: item = {} as Machine,
    bgColor,
    count,
    maxCount,
    isOperator,
    productionBatchId,
    insetTop = 0,
    insetBottom = 0,
    scale = initialScale,
    font = initialFont,
    correctQuantity,
  } = props;

  const status = item?.machineStatus?.status;

  const systemPopup = global['systemPopup'];

  const unknownNCFile = item?.machineStatus?.ncFileStatus === ncFileStatuses.Unknown;

  const navigation = useNavigation();
  const popupOpenRef = useRef<boolean>(false);

  const route = useRoute();
  const shouldHidePopupParam = route.params?.hideNcFilePopup;

  useFocusEffect(
    useCallback(() => {
      const unsubscribe = navigation.addListener('beforeRemove', () => {
        if (popupOpenRef.current) {
          systemPopup.hide();
          popupOpenRef.current = false;
        }
      });

      return unsubscribe;
    }, [systemPopup, navigation])
  );

  const calculatedTop = scale(OPERATOR_PANEL_PADDING + OPERATOR_PANEL_HEADER_HEIGHT + insetTop);

  const renderContent = () => {
    return (
      <View
        style={{
          width: '100%',
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          paddingHorizontal: scale(OPERATOR_PANEL_PADDING),
        }}
      >
        <View style={{ position: 'absolute', top: calculatedTop, alignItems: 'center' }}>
          <HomePageTopCentralPart CC={CC} alarms={item?.machineStatus?.machineAlarms} scale={scale} font={font} />
        </View>
        <TourTarget tourId={WELCOME_TOUR_IDS.OP_COUNTER}>
          <HomePageCentralPart
            {...{
              CC,
              ncFileId: item.machineStatus?.ncFileId,
              machineStatus: item.machineStatus,
              ncFileName: item.machineStatus.ncFile,
              bgColor,
              count,
              maxCount,
              isOperator,
              selectedItem: item,
              productionBatchId,
              scale,
              font,
              correctQuantity,
            }}
          />
        </TourTarget>
        <TourTarget tourId={WELCOME_TOUR_IDS.OP_NC_FILE_INFO} style={{ position: 'absolute', left: scale(OPERATOR_PANEL_PADDING), top: calculatedTop }}>
          <NCFileJobsInfo
            ncFileName={item.machineStatus.ncFile}
            ncFileStatus={item.machineStatus.ncFileStatus}
            CC={CC}
            machineStatus={status}
            isOperator={isOperator}
            scale={scale}
            font={font}
          />
        </TourTarget>
        <View style={{ position: 'absolute', top: calculatedTop, right: scale(OPERATOR_PANEL_PADDING) }}>
          <HomePageTopRightPart {...{ machineStatus: status, CC, ncFileStatus: item?.machineStatus?.ncFileStatus, isOperator, scale, font }} />
        </View>
        <TourTarget
          tourId={WELCOME_TOUR_IDS.OP_BOTTOM_ACTIONS}
          style={{
            position: 'absolute',
            bottom: scale(OPERATOR_PANEL_PADDING + insetBottom),
            paddingHorizontal: !isWeb && !isPhone ? 0 : scale(OPERATOR_PANEL_PADDING),
            width: '100%',
          }}
        >
          <HomePageBottomPart
            {...{
              CC,
              machineStatus: status,
              isOperator,
              ncFileStatus: item.machineStatus.ncFileStatus,
              ncFileName: item.machineStatus.ncFile,
              ncFileId: item.machineStatus.ncFileId,
              selectedItem: item,
              productionBatchId,
              autoOpenNCFileModal: unknownNCFile,
              systemPopup,
              shouldHidePopupParam,
              scale,
              font,
            }}
          />
        </TourTarget>
      </View>
    );
  };

  return (
    <View style={[{ backgroundColor: bgColor, height: '100%' }]}>
      <HomePageHeader {...{ CC, machineName: item.fullName, machineStatus: status, isOperator, insetTop, scale, font }} />
      {renderContent()}
    </View>
  );
};

export default connect(null, { setModalMissingPIOpen })(Panel);
